import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { canSeeTask } from "../lib/roles";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { WarningCircle, SignIn, SignOut } from "@phosphor-icons/react";
import PinPad from "./PinPad";

function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function IncidenceDialog({ worker, onClose, prefilledPin = "" }) {
  const [kind, setKind] = useState("fichaje"); // "fichaje" | "tarea"
  const [breakReason, setBreakReason] = useState("");
  const [breakReasons, setBreakReasons] = useState([]);
  const [date, setDate] = useState(todayIso());
  const [changeEntry, setChangeEntry] = useState(false);
  const [changeExit, setChangeExit] = useState(false);
  const [entryTime, setEntryTime] = useState("09:00");
  const [exitTime, setExitTime] = useState("17:00");
  const [motivo, setMotivo] = useState("");
  const [pin, setPin] = useState(prefilledPin);
  const [sending, setSending] = useState(false);
  const [recentIncidences, setRecentIncidences] = useState([]);
  const pinPrefilled = Boolean(prefilledPin);

  // Load break reasons (shared public endpoint) on first mount
  useEffect(() => {
    let alive = true;
    api.get("/settings/public/break-reasons")
      .then((r) => {
        if (!alive) return;
        const reasons = (r.data?.reasons || [])
          .map((x) => (typeof x === "string" ? { name: x, visible_roles: ["todos"] } : x))
          .filter((x) => canSeeTask(worker, x))
          .sort((a, b) => (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" }));
        setBreakReasons(reasons);
        if (reasons.length > 0 && !breakReason) setBreakReason(reasons[0].name);
      })
      .catch(() => { if (alive) setBreakReasons([]); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the PIN is pre-filled (opened from WorkerHistoryDialog), fetch recent
  // incidences so the worker sees any previous rejections with their motive.
  useEffect(() => {
    if (!pinPrefilled) return;
    let alive = true;
    api
      .post("/incidences/mine", { user_id: worker.id, pin: prefilledPin })
      .then((r) => { if (alive) setRecentIncidences(r.data || []); })
      .catch(() => { if (alive) setRecentIncidences([]); });
    return () => { alive = false; };
  }, [pinPrefilled, worker.id, prefilledPin]);

  const submit = async () => {
    if (!changeEntry && !changeExit) {
      toast.error("Marca al menos una hora a corregir");
      return;
    }
    if (kind === "tarea" && !breakReason) {
      toast.error("Selecciona la tarea afectada");
      return;
    }
    if (!pin) {
      toast.error("Introduce tu PIN");
      return;
    }
    setSending(true);
    try {
      await api.post("/incidences", {
        user_id: worker.id,
        pin,
        kind,
        break_reason: kind === "tarea" ? breakReason : null,
        date,
        change_entry: changeEntry,
        change_exit: changeExit,
        entry_time: changeEntry ? entryTime : null,
        exit_time: changeExit ? exitTime : null,
        motivo,
      });
      toast.success("Incidencia enviada. Un admin la revisará.");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="incidence-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <WarningCircle size={22} weight="duotone" className="text-[var(--primary)]" />
            Incidencia — {worker.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {recentIncidences.length > 0 && (
            <div className="rounded-xl border border-[var(--border)] bg-[var(--bg)] p-3" data-testid="incidence-recent-list">
              <div className="label-cap mb-2">tus incidencias recientes</div>
              <ul className="space-y-1.5 max-h-40 overflow-y-auto">
                {recentIncidences.slice(0, 4).map((inc) => {
                  const isTarea = (inc.kind || "fichaje") === "tarea";
                  const badge =
                    inc.status === "aprobado"
                      ? "bg-emerald-100 text-emerald-700 border-emerald-300"
                      : inc.status === "denegado"
                      ? "bg-red-100 text-red-700 border-red-300"
                      : "bg-yellow-100 text-yellow-700 border-yellow-300";
                  const kindBadge = isTarea
                    ? "bg-indigo-100 text-indigo-700 border-indigo-300"
                    : "bg-slate-100 text-slate-700 border-slate-300";
                  const parts = [];
                  if (inc.change_entry && inc.entry_time) parts.push(`${isTarea ? "I" : "E"} ${inc.entry_time}`);
                  if (inc.change_exit && inc.exit_time) parts.push(`${isTarea ? "F" : "S"} ${inc.exit_time}`);
                  return (
                    <li key={inc.id} className="text-xs bg-white rounded-lg border border-[var(--border)] px-2 py-1.5" data-testid={`incidence-recent-${inc.id}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display font-semibold">{inc.date}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border uppercase tracking-wider font-display font-bold ${kindBadge}`}>
                          {isTarea ? "Tarea" : "Fichaje"}
                        </span>
                        {isTarea && inc.break_reason && (
                          <span className="text-[var(--text-secondary)]">{inc.break_reason}</span>
                        )}
                        <span className="text-[var(--text-secondary)]">{parts.join(" · ") || "—"}</span>
                        <span className={`ml-auto capitalize text-[10px] px-2 py-0.5 rounded-full border ${badge}`}>{inc.status}</span>
                      </div>
                      {inc.status === "denegado" && inc.motivo_rechazo && (
                        <div className="mt-1 text-red-700" data-testid={`incidence-recent-rechazo-${inc.id}`}>
                          <span className="font-semibold">Rechazada:</span> “{inc.motivo_rechazo}”
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          <div>
            <Label className="label-cap">Tipo de incidencia</Label>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className="mt-2 w-full px-3 py-2 rounded-lg border border-[var(--border)] bg-white text-sm font-display font-semibold"
              data-testid="incidence-kind"
            >
              <option value="fichaje">Fichaje (entrada/salida)</option>
              <option value="tarea">Tarea (descanso)</option>
            </select>
          </div>

          {kind === "tarea" && (
            <div>
              <Label className="label-cap">Tarea afectada</Label>
              <select
                value={breakReason}
                onChange={(e) => setBreakReason(e.target.value)}
                className="mt-2 w-full px-3 py-2 rounded-lg border border-[var(--border)] bg-white text-sm font-display font-semibold"
                data-testid="incidence-break-reason"
              >
                {breakReasons.length === 0 ? (
                  <option value="">(sin tareas configuradas)</option>
                ) : (
                  breakReasons.map((r) => (
                    <option key={r.name} value={r.name} data-testid={`incidence-break-reason-opt-${r.name}`}>
                      {r.name}
                    </option>
                  ))
                )}
              </select>
            </div>
          )}

          <div>
            <Label className="label-cap">Fecha</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              max={todayIso()}
              className="mt-2"
              data-testid="incidence-date"
            />
          </div>

          <div className="space-y-3">
            <label
              className="flex items-center gap-3 p-3 rounded-lg border border-[var(--border)] cursor-pointer hover:border-[var(--primary)]"
              data-testid="incidence-toggle-entry"
            >
              <Checkbox
                checked={changeEntry}
                onCheckedChange={(v) => setChangeEntry(!!v)}
              />
              <SignIn size={18} weight="duotone" className="text-[#4A5D23]" />
              <div className="flex-1">
                <div className="font-display font-semibold text-sm">
                  {kind === "tarea" ? "Hora de Inicio" : "Hora de Entrada"}
                </div>
                {changeEntry && (
                  <Input
                    type="time"
                    value={entryTime}
                    onChange={(e) => setEntryTime(e.target.value)}
                    className="mt-2"
                    data-testid="incidence-entry-time"
                  />
                )}
              </div>
            </label>
            <label
              className="flex items-center gap-3 p-3 rounded-lg border border-[var(--border)] cursor-pointer hover:border-[var(--primary)]"
              data-testid="incidence-toggle-exit"
            >
              <Checkbox
                checked={changeExit}
                onCheckedChange={(v) => setChangeExit(!!v)}
              />
              <SignOut size={18} weight="duotone" className="text-[#E07A5F]" />
              <div className="flex-1">
                <div className="font-display font-semibold text-sm">
                  {kind === "tarea" ? "Hora de Fin" : "Hora de Salida"}
                </div>
                {changeExit && (
                  <Input
                    type="time"
                    value={exitTime}
                    onChange={(e) => setExitTime(e.target.value)}
                    className="mt-2"
                    data-testid="incidence-exit-time"
                  />
                )}
              </div>
            </label>
          </div>

          <div>
            <Label className="label-cap">Motivo</Label>
            <Textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Explica brevemente por qué solicitas el cambio"
              className="mt-2"
              rows={3}
              data-testid="incidence-motivo"
            />
          </div>

          {!pinPrefilled && (
            <div>
              <Label className="label-cap">Tu PIN</Label>
              <div className="mt-2">
                <PinPad
                  value={pin}
                  onChange={setPin}
                  maxLength={11}
                  testIdPrefix="incidence-pinpad"
                  inputTestId="incidence-pin"
                  placeholder="••••"
                />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            className="btn-primary"
            onClick={submit}
            disabled={sending}
            data-testid="incidence-submit"
          >
            {sending ? "Enviando…" : "Enviar incidencia"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
